/**
 * Club Central's @mediatechgroup/site-host configuration: the ONE place the
 * platform's hosts live for the web app (PM b5bc6744).
 *
 * apps/backend/src/config/siteHost.js carries the SAME values, and the public
 * website app will take a third copy; src/api/__tests__/hostResolver.pinned.test.ts
 * holds the web and backend copies identical. Change them together.
 *
 * The package bakes in no company domain: every convention below is ours.
 * Reserved labels are one concept across the repository: slugFromHost never
 * resolves one, and a provisioner must never create a club on one.
 */
import type { SiteHostConfig } from "@mediatechgroup/site-host";

/** The apex under which a club's branded subdomain is `<slug>.clubcentral.app`. */
export const TENANT_BASE_DOMAIN = "clubcentral.app";

/** The member/staff web app's production host and its Netlify branch-deploy (staging) host. */
export const WEB_APP_HOST = "clubs.clubcentral.app";
export const WEB_APP_STAGING_HOST = "develop--sports-team-manager.netlify.app";

/**
 * Hosts that are the platform itself, never a club's front door. The retired
 * staging.clubs / develop.clubs custom hosts are gone: being two labels deep
 * they never resolved a slug, which the frozen matrix proves.
 */
export const PLATFORM_HOSTS: readonly string[] = [
  "clubcentral.app",
  "www.clubcentral.app",
  WEB_APP_HOST,
  "manage.clubcentral.app",
  "localhost",
  "127.0.0.1",
];

/** The Netlify branch-deploy hosts staging runs on (the develop branch). */
export const STAGING_HOSTS: readonly string[] = [
  WEB_APP_STAGING_HOST,
  "develop--manage-club-central.netlify.app",
  "develop--athletics-club-platform.netlify.app",
];

/** Infrastructure suffixes no club can be served under (deploy previews included). */
export const PLATFORM_HOST_SUFFIXES: readonly string[] = [".netlify.app"];

/** Subdomain labels under clubcentral.app reserved for platform use; never a club slug. */
export const RESERVED_LABELS: readonly string[] = [
  "clubs",
  "www",
  "staging",
  "develop",
  "app",
  "manage",
  "api",
  "admin",
];

export const SITE_HOST_CONFIG: SiteHostConfig = {
  stagingBaseDomain: TENANT_BASE_DOMAIN,
  stagingHosts: [...STAGING_HOSTS],
  platformHosts: [...PLATFORM_HOSTS],
  platformHostSuffixes: [...PLATFORM_HOST_SUFFIXES],
  tenantBaseDomains: [TENANT_BASE_DOMAIN],
  reservedSubdomains: [...RESERVED_LABELS],
  // Empty until a club's DNS provider forwards its apex to www.
  wwwCanonicalApexes: [],
};

/**
 * How the web app reads a club from the browser's hostname: the shared config
 * plus two web-only rules (the backend's CORS check reads neither).
 * window.location.hostname carries no port and no trailing dot, so "exact"
 * keeps a host that has either from naming a club, and no club slug is
 * shorter than two characters (isValidTenantSlug agrees).
 */
export const HOST_SLUG_CONFIG: SiteHostConfig = {
  ...SITE_HOST_CONFIG,
  slugMatch: "exact",
  minSlugLength: 2,
};
